import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem,
  FormHelperText, Table, TableHead, TableRow, TableCell, TableBody,
  Tooltip, Checkbox, Box, IconButton
} from '@mui/material';
import Typography from '@mui/material/Typography';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { HiMiniPlus } from "react-icons/hi2";
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import './AddQuestionPartSection.css';
import { assessmentService } from '../../service/assessment/assessmentService';
import PreviewQuestionView from './PreviewQuestionView';

const AddQuestionPartSection = ({
  open,
  onClose,
  assessment_parts,
  assessment_sections,
  assessmentFilteredQuestions,
  selectedAssessmentId,
  addPartSectionLoading,
  onSuccess,
  containerName
}) => {
  const [selectedAssessmentPart, setSelectedAssessmentPart] = useState('');
  const [selectedAssessmentSection, setSelectedAssessmentSection] = useState('');
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [loading, setLoading] = useState(addPartSectionLoading);
  const [visibleQuestionIds, setVisibleQuestionIds] = useState([]);

  useEffect(() => {
    setLoading(addPartSectionLoading);
  }, [addPartSectionLoading]);

  const toggleQuestionSelect = (id) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(id) ? prev.filter(qid => qid !== id) : [...prev, id]
    );
  };

  const toggleQuestionPreview = (id) => {
    setVisibleQuestionIds((prev) =>
      prev.includes(id) ? prev.filter(qid => qid !== id) : [...prev, id]
    );
  };

  //Collect all subquestion IDs used in any question
  const allSubQuestionIds = new Set();
  assessmentFilteredQuestions.forEach(q => {
    if (q.sub_questions) {
      q.sub_questions.split(',').forEach(id => allSubQuestionIds.add(parseInt(id.trim())));
    }
  });

  // Only show questions that are NOT subquestions
  const mainQuestionsOnly = assessmentFilteredQuestions.filter(
    q => !allSubQuestionIds.has(q.question_id)
  );

  const areAllSelected = mainQuestionsOnly.length > 0 && selectedQuestionIds.length === mainQuestionsOnly.length;
  const isIndeterminate = selectedQuestionIds.length > 0 && selectedQuestionIds.length < mainQuestionsOnly.length;

  const toggleSelectAll = () => {
    if (areAllSelected) {
      setSelectedQuestionIds([]);
    } else {
      setSelectedQuestionIds(mainQuestionsOnly.map(q => q.question_id));
    }
  };

  const allCheckboxesDisabled = mainQuestionsOnly.every(q => q.section_id !== null);

  const handleBulkAddQuestions = async () => {
    if (!selectedAssessmentId || !selectedAssessmentPart || !selectedAssessmentSection || selectedQuestionIds.length === 0) {
      window.Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Please select a part, section, and at least one question!',
      });
      return;
    }

    // Collect all subquestion IDs to include in payload
    const allSelected = new Set();
    selectedQuestionIds.forEach(qid => {
      allSelected.add(qid);
      const mainQ = assessmentFilteredQuestions.find(q => q.question_id === qid);
      if (mainQ?.sub_questions) {
        mainQ.sub_questions.split(',').forEach(id => allSelected.add(parseInt(id.trim())));
      }
    });

    const payload = {
      assessment_id: selectedAssessmentId,
      section_id: selectedAssessmentSection,
      question_ids: Array.from(allSelected),
    };

    try {
      setLoading(true);
      const response = await assessmentService.updateAssessmentQuestionSection(payload);

      if (response.success) {
        window.Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Questions moved to selected part/section successfully!'
        });
        setSelectedAssessmentPart('');
        setSelectedAssessmentSection('');
        setSelectedQuestionIds([]);
        onClose();
        onSuccess();
      } else {
        window.Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: 'Failed to move questions to selected part/section!',
        });
      }
    } catch (err) {
      window.Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while moving questions to selected part/section!',
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredSections = assessment_sections.filter(
    sec => sec.assessment_part_id === parseInt(selectedAssessmentPart)
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" className="aqps-dialog">
      <DialogTitle>Update Question Part/Section</DialogTitle>
      <DialogContent dividers style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <Typography
          variant="body1"
          color="success"
          sx={{ mb: 2, fontWeight: 'bold', textAlign: 'left' }}
        >
          Please select the questions to update part/section
        </Typography>

        <div className="aqps-selects-container">
          <FormControl fullWidth margin="dense" className="aqps-select">
            <InputLabel id="part-select-label">Select Part</InputLabel>
            <Select
              labelId="part-select-label"
              value={selectedAssessmentPart}
              onChange={(e) => {
                setSelectedAssessmentPart(e.target.value);
                setSelectedAssessmentSection('');
              }}
            >
              {assessment_parts.length > 0 ? (
                assessment_parts.map((part) => (
                  <MenuItem key={part.assessment_part_id} value={part.assessment_part_id}>
                    {part.part_title}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>No parts available</MenuItem>
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense" className="aqps-select">
            <InputLabel id="section-select-label">Select Section</InputLabel>
            <Select
              labelId="section-select-label"
              value={selectedAssessmentSection}
              onChange={(e) => setSelectedAssessmentSection(e.target.value)}
              disabled={!selectedAssessmentPart}
            >
              {filteredSections.length > 0 ? (
                filteredSections.map((sec) => (
                  <MenuItem key={sec.part_section_id} value={sec.part_section_id}>
                    {sec.section_title}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>No sections available</MenuItem>
              )}
            </Select>
            {!selectedAssessmentPart && (
              <FormHelperText>Please select a part to load sections.</FormHelperText>
            )}
          </FormControl>
        </div>

        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell>
                <Box display="flex" alignItems="center">
                  <Tooltip
                    title={allCheckboxesDisabled ? "All questions are already assigned to a section" : ""}
                    arrow
                  >
                    <span>
                      <Checkbox
                        checked={areAllSelected}
                        indeterminate={isIndeterminate}
                        onChange={toggleSelectAll}
                        disabled={allCheckboxesDisabled}
                        size="small"
                      />
                    </span>
                  </Tooltip>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', ml: 1 }}>
                    Select All
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>Question Code</TableCell>
              <TableCell>Question Title</TableCell>
              <TableCell>Question Type</TableCell>
              <TableCell>Assigned Part</TableCell>
              <TableCell>Assigned Section</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mainQuestionsOnly.map((q) => {
              const section = assessment_sections.find(sec => sec.part_section_id === q.section_id);
              const part = section ? assessment_parts.find(p => p.assessment_part_id === section.assessment_part_id) : null;

              return (
                <React.Fragment key={q.question_id}>
                  <TableRow>
                    <TableCell>
                      <Tooltip
                        title={q.section_id !== null ? "Question already assigned to a section" : ""}
                        arrow
                        placement="top"
                      >
                        {/* Wrapping span is needed when disabling interactive elements inside Tooltip */}
                        <span>
                          <Checkbox
                            checked={selectedQuestionIds.includes(q.question_id)}
                            onChange={() => toggleQuestionSelect(q.question_id)}
                            disabled={q.section_id !== null}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{`QUSTN${q.question_id}`}</TableCell>
                    <TableCell>{q.question_number}.{q.title}</TableCell>
                    <TableCell>{q.question_type}{q.sub_questions !== null && <span style={{ color: '#888' }}> (Main Question)</span>}</TableCell>
                    <TableCell>{part ? part.part_title : 'N/A'}</TableCell>
                    <TableCell>{section ? section.section_title : 'N/A'}</TableCell>
                    <TableCell>
                      <Tooltip title={visibleQuestionIds.includes(q.question_id) ? 'Hide Preview' : 'View Preview'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => toggleQuestionPreview(q.question_id)}
                            className="aqps-icon-btn"
                            aria-label={visibleQuestionIds.includes(q.question_id) ? 'Hide' : 'View'}
                          >
                            {visibleQuestionIds.includes(q.question_id) ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {visibleQuestionIds.includes(q.question_id) && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <PreviewQuestionView
                          question={q}
                          index={assessmentFilteredQuestions.findIndex(x => x.question_id === q.question_id)}
                          questionContents={assessmentFilteredQuestions.map(q => q.content)}
                          answerContents={assessmentFilteredQuestions.map(q => q.answer)}
                          allQuestions={assessmentFilteredQuestions}
                          containerName={containerName}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>

        {/* Bottom selects removed — top selects are used instead */}
      </DialogContent>
      <DialogActions
        className="aqps-actions"
        style={{ justifyContent: 'flex-end', padding: '16px 24px' }}
      >
        <button
          className="aqps-btn aqps-btn-primary"
          onClick={handleBulkAddQuestions}
          disabled={
            !selectedAssessmentSection ||
            selectedQuestionIds.length === 0 ||
            loading
          }
        >
          <HiMiniPlus style={{ marginRight: '6px' }} />
          {loading ? 'Updating...' : 'Update Part / Section'}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default AddQuestionPartSection;
